import { useEffect, useState } from "react";
import { Link } from "wouter";
import { X } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { getDict } from "@/lib/i18n";

const STORAGE_KEY = "gml-cookie-consent";

type Choice = "accepted" | "rejected";

function readChoice(): Choice | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    if (value === "accepted" || value === "rejected") return value;
    return null;
  } catch {
    return null;
  }
}

function writeChoice(value: Choice) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore storage errors (private mode, quota, etc.)
  }
}

export default function CookieBanner() {
  const { lang } = useLang();
  const c = getDict(lang).cookies;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Wait one tick so SSR/hydration mismatches don't surface and so the
    // banner doesn't flash before the layout settles.
    const timer = window.setTimeout(() => {
      if (readChoice() === null) setVisible(true);
    }, 200);
    return () => window.clearTimeout(timer);
  }, []);

  if (!visible) return null;

  const handleAccept = () => {
    writeChoice("accepted");
    setVisible(false);
  };

  const handleReject = () => {
    writeChoice("rejected");
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={c.ariaDialog}
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6 print:hidden"
    >
      <div className="container max-w-4xl">
        <div className="relative rounded-2xl border border-[var(--forest)]/15 bg-[var(--cream)] shadow-[0_20px_60px_-20px_rgba(15,42,33,0.35)] backdrop-blur">
          <button
            type="button"
            onClick={handleReject}
            aria-label={c.ariaClose}
            className="absolute right-3 top-3 text-[var(--forest-deep)]/50 hover:text-[var(--forest-deep)] transition-colors"
          >
            <X size={18} />
          </button>
          <div className="p-5 sm:p-7 md:flex md:items-start md:gap-8">
            <div className="flex-1 pr-6">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-2">
                {c.eyebrow}
              </p>
              <h2 className="font-display text-xl md:text-2xl text-[var(--forest-deep)] leading-tight mb-2">
                {c.title}
              </h2>
              <p className="text-sm text-[var(--forest-deep)]/80 leading-relaxed">
                {c.body}{" "}
                <Link
                  href="/cookies"
                  className="underline text-[var(--forest)] hover:text-[var(--forest-deep)]"
                >
                  {c.policyLink}
                </Link>
                .
              </p>
            </div>
            <div className="mt-5 md:mt-1 flex flex-col sm:flex-row md:flex-col gap-2 md:w-44 md:shrink-0">
              <button
                type="button"
                onClick={handleAccept}
                className="h-10 rounded-md bg-[var(--forest)] hover:bg-[var(--forest-deep)] text-[var(--cream)] text-sm font-semibold transition-colors"
              >
                {c.accept}
              </button>
              <button
                type="button"
                onClick={handleReject}
                className="h-10 rounded-md border border-[var(--forest)]/30 text-[var(--forest-deep)] text-sm font-semibold hover:bg-[var(--forest)]/5 transition-colors"
              >
                {c.reject}
              </button>
              <Link
                href="/cookies"
                className="h-10 leading-10 text-center text-xs font-medium tracking-wide text-[var(--forest-deep)]/70 hover:text-[var(--forest-deep)] underline underline-offset-4"
              >
                {c.more}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
